import { createRoute, createRouter, lazyRouteComponent } from '@tanstack/react-router';
import { tiposDeTransicion } from './ui/destinos';
import { rootRoute } from './routes/root';
import { Hoy } from './features/hoy';
import { Metas } from './features/metas';
import { Foco } from './features/foco';
import { Progreso } from './features/progreso';
import { Perfil } from './features/perfil';
import { DetalleHabito } from './features/habito';

/**
 * Rutas en código. Hoy, Metas, Foco, Progreso y Perfil son los cinco destinos de la
 * barra; el resto son pantallas que se abren desde ellos.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * QUÉ VA EN EL BUNDLE INICIAL Y QUÉ NO.
 *
 * Las seis primeras son **el producto**: lo que el usuario abre la app para hacer.
 * Van dentro, y se pagan.
 *
 * Las siete de abajo entran por `lazyRouteComponent`, y el criterio no es el tamaño
 * sino **la frecuencia**: el rewind se abre una vez al año; la revisión, una vez a la
 * semana; el calendario, el círculo y el copiloto son extensiones de la v2 que muchos
 * usuarios no activarán nunca; la cuenta se toca una vez en la vida del dispositivo; y
 * ajustes, que estaba arriba por costumbre, tampoco se abre para hacer nada.
 *
 * Hacerlas diferidas no es una optimización cosmética: el presupuesto del PRD 15.3 es
 * de 200 kB gzip en el arranque, y medido de verdad —los seis ficheros que el HTML
 * precarga, no solo dos— la v2 completa lo rozaba. La lección es la del benchmark, que
 * carga 846 kB a todo el mundo, incluidos 145 kB de panel de administración para quien
 * no es administrador.
 * ───────────────────────────────────────────────────────────────────────────
 */
const rutas = [
  { path: '/', component: Hoy },
  { path: '/metas', component: Metas },
  { path: '/foco', component: Foco },
  { path: '/progreso', component: Progreso },
  { path: '/perfil', component: Perfil },
  { path: '/habito/$habitId', component: DetalleHabito },

  // --- diferidas ------------------------------------------------------------
  /**
   * AJUSTES SE BAJA AQUÍ, y por el criterio de esta lista, no para hacer sitio.
   *
   * Estaba arriba, entre las que «se pagan», y no encaja: ahí van las que el
   * usuario abre la app PARA HACER. Ajustes no se abre para hacer nada — se
   * abre para cambiar el tema, el inicio del día o la piel, y luego no se vuelve
   * en semanas. Por frecuencia se parece mucho más a Cuenta, que está diferida
   * desde el principio, que a Hoy.
   *
   * Que además libere presupuesto es la consecuencia, no el motivo. Si el motivo
   * fuera el tamaño estaríamos aplicando justo el criterio que este fichero dice
   * dos veces que NO es el criterio.
   */
  {
    /** Las áreas: se abren de vez en cuando, como Ajustes. Mismo criterio. */
    path: '/areas',
    component: lazyRouteComponent(() => import('./features/areas'), 'Areas'),
  },
  {
    /** Conexiones: se abre al crear o revocar una clave, unas pocas veces en la vida. */
    path: '/conexiones',
    component: lazyRouteComponent(() => import('./features/conexiones'), 'Conexiones'),
  },
  {
    path: '/ajustes',
    component: lazyRouteComponent(() => import('./features/ajustes'), 'Ajustes'),
  },
  {
    path: '/revision',
    component: lazyRouteComponent(() => import('./features/revision'), 'Revision'),
  },
  {
    path: '/rewind/$anio',
    component: lazyRouteComponent(() => import('./features/rewind'), 'RewindPantalla'),
  },
  {
    path: '/cuenta',
    component: lazyRouteComponent(() => import('./features/cuenta'), 'Cuenta'),
  },
  {
    path: '/calendario',
    component: lazyRouteComponent(() => import('./features/calendario'), 'Calendario'),
  },
  {
    /** La Semana: se planea un par de veces por semana; el día a día vive en Hoy. */
    path: '/semana',
    component: lazyRouteComponent(() => import('./features/semana'), 'Semana'),
  },
  {
    /** El Diario: se abre desde Hoy, una vez al día y casi siempre al final. */
    path: '/diario',
    component: lazyRouteComponent(() => import('./features/diario'), 'Diario'),
  },
  {
    path: '/circulo',
    component: lazyRouteComponent(() => import('./features/circulo'), 'Circulo'),
  },
  {
    path: '/copiloto',
    component: lazyRouteComponent(() => import('./features/copiloto'), 'Copiloto'),
  },
] as const;

const routeTree = rootRoute.addChildren(
  rutas.map((ruta) =>
    createRoute({ getParentRoute: () => rootRoute, path: ruta.path, component: ruta.component }),
  ),
);

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA TRANSICIÓN DE PANTALLA · que cambiar de sitio sea un movimiento.
 *
 * Había entrada y no había salida. Cada pantalla llegaba con su cascada de
 * 240 ms, pero la anterior desaparecía en un fotograma, y eso es lo que hacía
 * que el gesto más repetido de la app —cambiar de pestaña— se sintiera a
 * trompicones por bien que estuviera la entrada. **Fluidez no es que una cosa
 * entre bien: es que una sustituya a otra.**
 *
 * Lo hace el navegador. `startViewTransition` saca una foto de lo que hay,
 * cambia el DOM y anima de una a otra; el router solo tiene que envolver la
 * navegación. Cero librerías y cero gestores de animación escritos a mano.
 *
 * EL RUMBO VIAJA COMO TIPO, y esa es la pieza que lo hace central. `types`
 * admite una función del viaje, así que el sentido se calcula UNA vez aquí y
 * lo cubre TODO —la barra, el riel, los enlaces de Perfil, el botón de atrás,
 * una navegación desde código—. La alternativa era un `onClick` por cada
 * `Link` de la app, que es la clase de cosa que se olvida en el siguiente
 * enlace que alguien añada.
 *
 * La regla del sentido vive en `ui/destinos.ts` y está probada: sale del orden
 * de la barra, no del historial. Y dentro de una misma sección devuelve
 * «ninguno» —abrir el Diario desde Hoy no es viajar—, que aquí se traduce en
 * una lista de tipos vacía: hay transición, pero sin deslizamiento. Y al
 * arrancar, sin pantalla de origen, no hay transición ninguna: en el iPhone
 * instalado se quedaba a medias y dejaba la app en negro (ver
 * `tiposDeTransicion`). El CSS está en `app.css`.
 *
 * DEGRADA SOLO. Donde no existe la API —hoy, Firefox— el router navega y ya,
 * exactamente como hasta ahora. Donde existe la API pero no los tipos, sale el
 * fundido por defecto. Nada que detectar y nada que apuntalar.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const router = createRouter({
  routeTree,
  defaultPreload: false,
  defaultViewTransition: {
    types: ({ fromLocation, toLocation }) =>
      tiposDeTransicion(fromLocation?.pathname, toLocation.pathname),
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
